import type { IconName } from '@/components/icons'

/**
 * 侧栏条目 —— **一个条目就是一个页面，没有第二层。**
 *
 * 这里曾经有过 `children: NavChild[]`，渲染成可展开的子项列表。
 * 2026-09-28 撤掉，理由是它同时犯了三个错：
 *
 *   1. **同一页面的多个入口摊在侧栏里**。文案子类 6 条、视频子类 3 条，
 *      点进去全是同一个页面、只是 query 不同 —— 用户看到的是「折叠树」，
 *      实际是同一页的三个门。旁白说「合并为一个页面就可以」，说的就是这个。
 *   2. **要点两次才到得了**。折叠子项的第一步永远不是目的地。
 *   3. **两份入口维护同一个东西**：页面里本来就有 chips / tab 承载这些选择
 *      （工具 chips / 渠道 chips / 场景 chips / 类型 chips），侧栏再铺一遍，
 *      改一处漏一处是注定的。
 *
 * 所以：**凡是能在一个页面里用控件切换的，一律不进侧栏。**
 * 设计稿里「具体功能」那一列的内容写进 `hint`，只用于**搜索**与悬浮提示 ——
 * 用户搜「换背景」「优惠券」「分镜」仍然找得到，但不占一行入口。
 */
export type NavItem = {
  id: string
  /** 落地页面。带 query 时表示「进这个页面的某个初始状态」，页面内可再切 */
  label: string
  to: string
  icon: IconName
  /** 这一类覆盖哪些具体功能。不做成可点条目，只用于搜索与 tooltip */
  hint?: string
}

export type NavGroup = { id: string; label: string; items: NavItem[] }

/**
 * 导航 IA —— 三个大类，类内**只有一层**。
 *
 * 定下来的规则，都是踩过坑的：
 *
 * 1. **一级类只有三个：图片 / 文案 / 视频**。第四类「模特 · 数字人」于 2026-09-29 撤销
 *    （用户原话「数字人视频放到视频这边，ai 模特放到 header 那边」）：
 *    它剩下的两条都不是「一类素材」—— **数字人**产出的是视频、上游与流程都跟视频同一套，
 *    归进视频类才不用在两个组之间来回找；**AI 模特**是一种跨类目的人像资产
 *    （商品图 / 服饰图 / 广告图都能带模特），不属于任何单一素材类，所以放进顶栏常驻，
 *    与「素材库」同级。空掉的分组必须删掉，留着就是一行没有内容的标题。
 *    四个大类的划分原本照抄公司《电商素材设计稿》，这次是**按内容归位**做的收敛，
 *    改的是「哪一类」，没改任何一条能力 —— 三个 slug 与页面一个都没动。
 *    「总览」既不属于素材大类，也不该占侧栏：它是工具与账本（首页 / 任务中心 /
 *    模板 / 作品 / 用量），跟「今天出什么素材」无关，放在侧栏底部要滚动才看得到。
 *    **这五项全部搬到顶栏常驻**，见 HEADER_ENTRIES。
 *
 * 2. **一个条目 = 一个页面（14 条业务路由）**。设计稿的「子类」若指向同一页面就并成一条；
 *    子类下的「具体功能」全部下放到页面内的控件。
 *
 * 3. **同一个页面不许出现两个入口**（哪怕 query 不同）。文案子类 6 条合一、
 *    视频的「模特带货」与「广告视频」合一、「数字人口播」与「数字人」合一、
 *    **「主图与详情」+「商品套图」+「平台图」三条合一**、
 *    **「带货短视频」+「商品视频」两条合一**，都是这条规则的产物。
 *    冒烟测试里钉死了。
 *
 * 4. **视频类内部按输入物切**：「带货短视频」与「商品视频」都是**从文案/产品图生成**
 *    （15 秒竖版带货 / 8 秒方版展示），合并为一页，档位在页面内切；
 *    「视频复刻」输入物相反（**必传参考视频**，复刻既有结构），仍是独立条目 ——
 *    用户原话「视频复刻不变」。
 *
 * 5. **页面数靠 query 参数压，不靠加路由**。参考站把 56 个功能做成 56 条路由，
 *    实现量与维护成本翻倍。这里收敛为 14 个真实页面，页面内状态用
 *    `?tool= / ?feature= / ?channel= / ?group= / ?mode= / ?type= / ?scene= / ?use=` 表达。
 *    （`/ai-set`、`/localize`、`/short-video`、`/main-video` 现在只是**旧地址别名**，
 *    重定向到携带模式的正式地址，不是页面、不进侧栏 —— 见 App.tsx。）
 *
 * ⚠️ 设计稿里的「视频处理」（自动剪辑 / 字幕 / 配音 / 翻译 / 横竖版转换）与
 *    「模特管理 / 数字人管理」**没有落进导航** —— 它们没有上游能力也没有页面，
 *    摆进侧栏就是死链。补这三组要先接上游，见 docs 里的缺口清单。
 */
export const NAV: NavGroup[] = [
  /* ====================== ① 图片 ====================== */
  {
    id: 'image',
    label: '图片',
    items: [
      {
        id: 'goods-image',
        label: '商品图',
        to: '/professional/products',
        icon: 'box',
        hint: '白底主图 / 商品场景图 / 卖点图 / 多角度 / 细节特写 / 包装礼盒 / 展台背景',
      },
      {
        id: 'main-set-detail',
        label: '主图 · 套图 · 详情',
        to: '/detail-page',
        icon: 'grid',
        // 三个页面（主图与详情 / 商品套图 / 平台图）于 2026-09-29 合并为本页。
        // 二次合并（同日）：一级入口收敛成「电商主图 / 详情页模块」两个。
        // 三次收敛（2026-09-30）：那行一级 chip-row 也删掉了 ——
        // 用户原话「详情页跟主图合并为一页，然后在输入的时候选择就可以」，
        // 4 个产出形态平铺在第一步的「产出形态」控件里。
        // 同日下架「详情页长图」（原话「详情的长图不搞先」），所以 hint 里不再写它
        // —— 下架的能力不该继续在导航里宣传（用户搜「长图」会落到这一页，
        // 而这一页已经出不了长图）。
        // 同日先下架「多平台合规版」、再整块下架「派生」（原话「派生这个弄太复杂了，不要了」）。
        // 同日再下架「商品套图」（原话「这套图跟详情页重复了」），hint 里一并去掉 ——
        // 同一条规矩：**下架的能力不该继续在导航里宣传**。
        hint: '电商主图 / 平台详情模块（含 A+） / 模仿版式',
      },
      {
        id: 'social-image',
        label: '社媒图',
        to: '/social-image',
        icon: 'star',
        hint: '小红书种草图 / 朋友圈配图 / Instagram · FB 配图',
      },
      {
        id: 'ad-image',
        label: '广告图',
        to: '/banner',
        icon: 'layout',
        hint: 'Banner / 直播海报 / 促销海报 / 优惠券图',
      },
      {
        id: 'fashion-image',
        label: '服饰图',
        to: '/professional/fashion',
        icon: 'shirt',
        hint: '上身图 / 平铺图 / 挂拍图 / 细节图 / 换装图 / 服装模特',
      },
      {
        id: 'image-tools',
        label: '图片处理',
        to: '/img-localize',
        icon: 'wand',
        hint: '去背景 / 换背景 / 超清放大 / 扩图 / 精修 / 去水印',
      },
    ],
  },

  /* ====================== ② 文案 ====================== */
  {
    id: 'copy',
    label: '文案',
    items: [
      {
        id: 'copywriting',
        label: '文案工作台',
        to: '/copywriting',
        icon: 'text',
        hint:
          '商品卖点提炼 / 商品与 SEO 标题 / 详情页文案 / 主图卖点关键词 / 小红书种草 / 朋友圈 / ' +
          'Instagram · Facebook / 广告文案 / 直播口播稿 / 短视频与分镜脚本 / 翻译本地化',
      },
      {
        id: 'tts',
        label: '语音合成',
        to: '/tts',
        icon: 'wave',
        // 2026-10-08 新开。**不与「文案工作台」合并**，判据两条：
        //   ① 产出形态不同：那边出的是字（给人看），这里出的是声音（给机器读）——
        //      下游完全不同（拷进后台 / 喂给数字人与剪辑当音轨）；
        //   ② 输入物相反（铁律里明文允许独立成页的那条）：文案页要产品名 / 卖点 /
        //      人群 / 平台，这一页只要「一段稿子 + 一个音色」。
        // 放在「文案」组下而不是「视频」组：它的输入物就是一份文案 ——
        // 用户写口播稿时人在文案这一组里，配音是紧接着的下一步，
        // 挪到视频组会让「写稿 → 配音」中间隔一次跨组跳转。
        hint: '口播稿转 mp3 / 直播配音 / 短视频旁白 / 数字人口播的语音 · 音色在页面里试听与挑选',
      },
      {
        id: 'voice-clone',
        label: '声音克隆',
        to: '/tts/clone',
        icon: 'mic',
        /*
          2026-10-09 新开。**不塞进 `/tts` 页的「音色库」抽屉**，判据两条：
            ① 参考站（`minimax.cn/audio/voices-cloning`）自己就是独立页 ——
               它的左侧栏里「音色库」与「声音克隆」是并列的两项；
            ② 两个动作的心智不同：音色库是「**挑**音色」（为合成服务），
               这里「**造**音色」（一段录音换一个可复用的 voice_id），
               输入物、产出物、合规红线（声音权授权）没有一件共用。
          与「语音合成」相邻而不是并进它：配音这条链是「造音色 → 用它念稿」，
          顺序上克隆在前，摆在一起才不会让用户先在合成页找不到音色、
          再回头找「音色是从哪来的」。
        */
        hint: '录一段自己的声音建音色 / 声音复刻 / 音色克隆 · 建好的音色在「语音合成」里挑来配音',
      },
    ],
  },

  /* ====================== ③ 视频 ====================== */
  {
    id: 'video',
    label: '视频',
    items: [
      {
        id: 'video-studio',
        label: '带货短视频 · 商品视频',
        to: '/video-studio',
        icon: 'video',
        // 两条入口（/short-video 与 /main-video）于 2026-09-29 合并为本页；
        // 2026-10-05 连页面内的档位 chip-row 也撤了 —— 两档并成一档
        // （用户原话「感觉差不多啊，需要合并」，判定见 config/videoModes.ts 头部）。
        // 搜索仍要能找到原来那两个名字，所以 hint 把两档覆盖的功能逐个写全。
        hint:
          'TVC 品牌质 / UGC 软广 / 纯带货口播 / 种草氛围 / 模特手持产品 / 模特使用产品 / ' +
          '信息流广告 / 促销视频 / 商品主图转视频 / 产品展示 / 360° 旋转展示 / ' +
          '场景展示 / 细节推近',
      },
      {
        id: 'digital-human',
        label: '数字人',
        to: '/digital-human',
        icon: 'shield',
        // 2026-09-29 从「模特 · 数字人」组移入视频组：它产出的就是视频，
        // 上游与流程跟视频同一套（用户原话「数字人视频放到视频这边」）。
        // 2026-10-05 接上 RunningHub InfiniteTalk 后 hint 里删掉「声音克隆」——
        // MiniMax TTS 没有这个能力，留着就是宣传一个点进去找不到的功能。
        hint: '数字人讲解 / 数字人带货 / 真人出镜口播（对口型）—— 用途与形象在页面内切换',
      },
      {
        id: 'video-replicate',
        label: '视频复刻',
        to: '/video-replicate',
        icon: 'copy',
        // 不与上面那条合并：输入物相反（必传参考视频，复刻既有结构）——
        // 用户原话「视频复刻不变」，所以入口、路由、草稿与任务键都保持独立。
        hint: '上传参考视频，复刻镜头结构与节奏，换掉产品',
      },
    ],
  },
]

/* -------------------------------------------------------------------------- */
/* 顶栏常驻入口                                                                */
/* -------------------------------------------------------------------------- */

/**
 * 工具与账本 + 一条跨类目资产 —— 常驻顶栏，不进侧栏。
 *
 * 为什么单独一份清单而不是塞进 NAV：
 *
 * - 前三项跟「今天出什么素材」无关，是账号级的工具（模板 / 作品 / 用量）
 *   与两个全局面板（首页 / 任务队列）。侧栏只放业务导航，口径才干净：
 *   进侧栏的都是「能做哪一类素材」。
 * - **`ai-model` 是 2026-09-29 之后唯一的例外**，用户原话「ai 模特放到 header 那边」。
 *   它确实产出素材，但**不属于任何一类**：商品图 / 服饰图 / 广告图都能带模特，
 *   挂在「图片」组下会让另外两组的人找不到它；原来那个「模特 · 数字人」组
 *   就是为它和数字人硬撑出来的第四类，而数字人已归入视频类 —— 剩下的它
 *   单独占一个分组更没道理。放到顶栏 = 与「素材库」同级：**跨页面的通用资产入口**。
 *
 * `surface` 记录每一项由哪个控件承载 —— 外观不同，但都必须「找得到」：
 *   brand    —— 左上角品牌区，点 logo 回首页
 *   queue    —— 「任务中心」按钮，**点一次直接落到 `/jobs`**（2026-10-05 改）
 *   link     —— 顶栏直链
 *   account  —— **账号胶囊下拉菜单**（2026-10-07 加）。放这里的理由与别的条目相反：
 *               它不是「今天出什么素材」，也不是随时要点的全局工具，而是**账号自己的
 *               那一页** —— 用户找它时眼睛在右上角那颗头像上，不在顶栏那一排按钮里。
 *   settings —— **设置弹窗**（2026-10-09 加）。这两条（我的作品 / 用量与明细）与
 *               `account` 是同一类「回看」页面，但再拆一层：
 *                 · `account` 管的是**账号本身**（额度账本、退出登录）；
 *                 · `settings` 管的是**我产出与花费了什么** —— 是工作记录，不是账号设置。
 *               2026-10-09 用户口径：「header 固定放商品资产，我的作品跟用量与明细
 *               放到设置那边」。放顶栏直链的问题不是不好找，是**顶栏那排会被挤到换行**；
 *               而它们的使用频率（偶尔回看）也撑不起一个常驻位。
 *               挂到账号菜单下会被误当成账号功能，所以单独给了一个承载面 ——
 *               **承载面不同就得是不同的 surface 值**，否则闸门查不出「声明了却没有 UI」。
 * 之所以把 `surface` 也写进配置：只删/改一处 UI 而忘了另一处时，
 * 冒烟测试能立刻报错，而不是等用户发现某个页面点不到。
 * （`account` 的兜底断言见 `scripts/smoke-nav.ts` 的「账号菜单必须消费 account 条目」；
 *   `settings` 的同款断言是「设置弹窗必须消费 settings 条目」。）
 *
 * ⚠️ `queue` 曾经叫 `popover`（点开是一个队列浮层）。2026-10-05 用户原话
 * 「任务中心点击直接跳转页面」—— 浮层里是 8 条最近任务 + 一句「查看全部任务 →」，
 * 而用户要看的正是**全部**，那第一步点击零信息量。浮层整块下线，按钮改成跳转。
 * 名字跟着行为走：留着 `popover` 这个词，下一个读代码的人会去找一个不存在的浮层。
 *
 * ⚠️ 这里曾经有一项 `{ id: 'admin', surface: 'link', adminOnly: true }`，
 * 2026-10-07 拆表时**连同 `adminOnly` 字段一起删掉**了。理由是「会员不可能进后台」
 * 从一条业务规则变成了**类型事实**：
 *
 *   · 拆表前「是不是管理员」是 `user.role` 的一个值，所以顶栏按角色过滤是成立的；
 *   · 拆表后后台账号住在 `admins` 表、走 `/api/admin/auth/*`，**会员账号在那一侧
 *     根本不存在**。于是顶栏里那个入口对每一个能看到顶栏的人都是死链 ——
 *     点进去是后台登录页，让用户拿一个会员账号去登一套他没有的账号。
 *
 * 所以 `/admin` 现在是**只有敲地址才到**的页面（`AdminRoute` 自带门卫，
 * 见 App.tsx），入口由用户自己记着。别「顺手」把它加回来：加回来就需要
 * `adminOnly` 这个过滤开关，而那个开关**永远只会滤掉全部人**。
 * 保留 `ROUTE_META['/admin']` —— 它仍是页面、仍要有标题，
 * 孤儿页检查里对它另有一条豁免（`scripts/smoke-nav.ts`）。
 */
export type HeaderEntry = NavItem & {
  surface: 'brand' | 'queue' | 'link' | 'account' | 'settings'
}

export const HEADER_ENTRIES: HeaderEntry[] = [
  {
    id: 'dashboard',
    label: '工作台首页',
    to: '/',
    icon: 'home',
    surface: 'brand',
    hint: '点左上角品牌区回到首页',
  },
  {
    id: 'jobs',
    label: '任务中心',
    to: '/jobs',
    icon: 'activity',
    surface: 'queue',
    hint: '全局队列 · 跨页面共享，切页不中断',
  },
  {
    id: 'ai-model',
    label: 'AI 模特',
    // 2026-10-07 改指向。原先这一条是 `/professional/products?feature=product-model`
    // ——**生成表单**。用户口径：「点击这个 AI 模特应该去一个新页面，是全部已经
    // 生成的 AI 模特的页面，可以搜索跟筛选的」。
    //
    // 也就是说这个入口的**语义变了**：从「去生成一个模特」变成「看你生成过的模特」。
    // 它现在与 `my-works` / `usage-audit` 同类 —— 是**回看**，不是生产。
    // 生成入口没有消失，挪进了那一页的「生成新模特」按钮（`pages/AiModels.tsx`）；
    // 模板 `st-04`（美妆上妆效果图）也仍直指生成表单那一条，不受影响。
    to: '/ai-models',
    icon: 'users',
    surface: 'link',
    hint: '已生成的真人模特库 · 搜索与按市场 / 性别 / 年龄 / 体型 / 气质筛选（生成新模特在这一页里）',
  },
  {
    id: 'product-assets',
    label: '商品库',
    // 2026-10-08 加。**与 `ai-model` 同类**：都是「跨类目的通用资产入口」，
    // 所以同样放顶栏（与「素材库」同级），不进侧栏 —— 侧栏的口径是
    // 「能做哪一类素材」，而商品库是**资料**（一个商品一条档案：
    // 标题 / 卖点 / 语言 / 组图），不是一类产出。
    //
    // 它与「素材库」的区别是粒度，两条都要留：
    //   · 素材库 = 一张图一条（散图池），入口在「上传参考图」；
    //   · 商品库 = 一个商品一条，图是它的字段 —— 「主图 · 详情」页要的正是后者
    //     （一次要「标题 + 卖点 + 一组产品图」）。
    // 标签用「商品库」而不是「商品资产」：顶栏那一排四个字最省位置；
    // 页面标题（ROUTE_META）才用全称。
    to: '/product-assets',
    icon: 'box',
    surface: 'link',
    hint:
      '一个商品一条档案：标题 · 卖点 · 语言 · 组图 · SKU · 平台。支持单个新增与表格批量导入' +
      '（CSV + 图片按文件名归位）—— 「主图 · 详情」页可直接引用，不必自己输出产品图与文案',
  },
  {
    id: 'batch-image',
    label: '批量出图',
    // 2026-10-08 从侧栏挪到顶栏（用户口径：「发 header 上」）。
    //
    // 为什么它不该占侧栏一格：侧栏的口径是「能做**哪一类**素材」，而批量出图
    // 不是新的一类 —— 它是「同一类素材，一次做一批」。它的出图参数与
    // 「主图 · 套图 · 详情」三档**共用同一份**（`config/studioShape` +
    // `lib/detailPrompts`），产出也归到同一个 `sourcePage: 'detail-page'` 下。
    // 占着侧栏一格，反而像是一种独立的产出类型。
    //
    // ⚠️ 它与「商品库」是**两件事**，别因为都在顶栏就并起来：
    // 商品库是**资料**（一个商品一条档案），批量出图是**干活的地方**（一份 CSV 出一批）。
    // 详情页那一行指路链接（`DetailPageStudio`）继续保留 —— 顶栏是「找得到」，
    // 那里是「正要批量时顺手拐进去」。
    to: '/batch',
    icon: 'layers',
    surface: 'link',
    hint:
      '一份 CSV 出一批：导入表格 / 逐行补参考图（按文件名归位）/ 分批提交，关掉页面回来接着改。' +
      '出图参数与「主图 · 套图 · 详情」共用同一份',
  },
  {
    id: 'hot-styles',
    label: '模板中心',
    // 2026-09-29：模板中心与首页**是同一页**（用户要求「首页改为模板中心为主」），
    // 所以这里直接指 `/` —— `/hot-styles` 退化为重定向别名。
    // 不删这一项：它是顶栏里唯一写着「模板」字样的按钮，用户找模板时先看这里；
    // 删了就只能靠点 logo 回首页，等于把一个明确的功能名藏起来。
    to: '/',
    icon: 'sparkles',
    surface: 'link',
    hint: '预设风格，一键套用（模板墙就在首页）',
  },
  {
    id: 'my-works',
    label: '我的作品',
    to: '/my-works',
    icon: 'folder',
    // 2026-10-09 从顶栏直链挪进**设置弹窗**（用户原话「我的作品跟用量与明细
    // 放到设置那边」）。它此前是顶栏直链 —— 但「回看我已经出了什么」跟
    // 「今天要出什么素材」不是同一个动作的频率，占一个常驻位会把那一排挤到换行。
    // ⚠️ 别把它并进 `account`：那是账号本身（额度账本 / 退出登录），
    // 这一条是**产出记录**。两者混在一个菜单里，用户会以为作品是账号功能。
    surface: 'settings',
    hint: '全部生成结果 · 分类筛选 · 批量导出（入口在顶栏右侧的「设置」里）',
  },
  {
    id: 'usage-audit',
    label: '用量与明细',
    to: '/usage-audit',
    icon: 'chart',
    // 同上，2026-10-09 与「我的作品」一起进设置弹窗。
    // 它跟「可用额度」不是一回事：额度是**还剩多少**（账号胶囊里那颗数字），
    // 这里是**花到哪去了**（按模型与页面拆开的明细）—— 所以不并进账号菜单。
    surface: 'settings',
    hint: '调用次数 · 消耗金额 · 按模型与页面统计（入口在顶栏右侧的「设置」里）',
  },
  {
    id: 'account',
    label: '账号中心',
    to: '/account',
    icon: 'coin',
    // 2026-10-07 加。原先「额度账本」是账号菜单里的一个**弹层**，账本挤在
    // 52vh 的小框里还得内滚动 —— 用户原话「点开可以新页面」，于是升成整页
    // （`/pages/Account.tsx`），账号信息、可用/已用额度、完整账本都在这一页。
    // 它由账号胶囊下拉承载（`surface: 'account'`），**不占顶栏直链位**；
    // 顶栏那一排再加会换行。
    surface: 'account',
    hint: '账号信息 · 可用与已用额度 · 额度账本（每一笔进出账）· 退出登录',
  },
]

/** 顶栏按钮区渲染的那部分（首页归品牌区、任务中心归队列浮层） */
export const HEADER_LINKS = HEADER_ENTRIES.filter((e) => e.surface === 'link')

/**
 * 页面标题 —— 顶栏左侧那一行字。
 *
 * ⚠️ 这里**只有 `title`，没有 `desc`**。
 *
 * 曾经每个页面还有一句 `desc`：跟在标题后面、用一条竖线隔开的灰字
 * （比如「AI 模特库 | 全部已生成的真人模特 · 搜索与按模特特征筛选 · 一次…」）。
 * 2026-10-09 整块删掉，用户原话「这些废话可以去掉，我要打造简洁的应用」。
 *
 * 依据不是"这句写得不好"，而是**它对多数页面本来就是第二处**：
 *   · `/ai-models` 的 desc 与 `pages/AiModels.tsx` 的 `CardHead hint` 说的是同一件事
 *     （「一次生成 = 一个人」，卡片上看到两张图时最需要的正是这句）；
 *   · `/product-assets`、`/jobs`、`/my-works` 同理，页面自己那块卡片头写得更完整；
 *   · 而顶栏那句话占的是**每次进这一页都要读一遍**的位置。
 *
 * ⚠️ 别把 `desc` 加回来。要让用户知道「这一页是干什么的」，写在页面自己的
 * `CardHead hint` 上 —— 那是他正在操作的地方；顶栏只回答「我在哪」，
 * 而这个答案已经由那一排直链的高亮给出了（见 `components/TopBar.tsx` 的 `data-active`）。
 * 导航条目里那份 `hint` 仍然保留 —— 它服务于**搜索与悬浮提示**，是另一件事。
 */
export const ROUTE_META: Record<string, { title: string }> = {
  '/': { title: '模板中心' },
  '/jobs': { title: '任务中心' },
  '/banner': { title: '广告图' },
  '/detail-page': {
    title: '主图 · 详情',
    // 不写「亚马逊 A+」这类品牌绑定的页签名 —— 这个页面支持 17 个平台，
    // 页签名由平台档案决定（Amazon 显示 Amazon A+，淘宝显示「详情页模块」）。
    // 这里写死一份，等于给用户一个「只有亚马逊能用」的错误印象。
    //
    // 名字里保留「主图 / 详情」两个词：它们原来是两条侧栏入口
    // （主图与详情 / 商品套图 / 平台图），合并后只留一个，
    // 用户会以为另外几个能力被删了。
    // ⚠️ 「商品套图」与「详情页长图」于 2026-09-30 下架（原话「这套图跟详情页重复了」
    // 「详情的长图不搞先」）—— 下架的能力不许在导航里继续宣传，所以 `NAV` 里
    // 这一条的 `hint` 也同步去掉了这两个词。
  },
  '/batch': { title: '批量出图' },
  '/professional/fashion': { title: '服饰图' },
  '/professional/products': { title: '商品图' },
  '/img-localize': { title: '图片处理' },
  '/social-image': { title: '社媒图' },
  '/video-studio': {
    title: '带货短视频 · 商品视频',
    // 同 `/detail-page`：名字里保留两个词 —— 它们原来是两条侧栏入口，
    // 合并后只留一个词，用户会以为另一条能力被删了。
    // 2026-10-05 连页面内的档位也并掉了，这两个词因此成了「商品视频」这条能力
    // 在界面上**唯一的落脚处**（能力本身还在：场景清单 12 项与 mv-02 转台模板都保留），
    // 所以更不该只留一个词。
  },
  '/video-replicate': { title: '视频复刻' },
  '/digital-human': { title: '数字人' },
  '/copywriting': { title: '文案工作台' },
  '/tts': { title: '语音合成' },
  '/tts/clone': { title: '声音克隆' },
  '/my-works': { title: '我的作品' },
  '/ai-models': { title: 'AI 模特库' },
  '/usage-audit': { title: '用量与明细' },
  '/product-assets': { title: '商品资产' },
  // ⚠️ 这一条**没有导航入口**（顶栏那个「后台管理」在 2026-10-07 拆表时删了，
  // 见上面 HeaderEntry 那段）。留着它是因为 `/admin` 仍是**一个真实页面**：
  // 它有自己的外壳、自己的标题，`smoke-nav` 的孤儿页检查对它单独豁免。
  // 删掉它不会让页面消失，只会让这一页在顶栏显示成兜底的「华数云工作台」。
  '/admin': { title: '后台管理' },
  '/account': { title: '账号中心' },
}
